/**
 * cl-core/kst — 한국 시각(KST, UTC+9) 계산 «정본»(hc3 H1 · HC 정리 2026-10-24). 순수 TS.
 * ⚠️ 서버(UTC)에서 12/31 15:00 이후를 다음 해로 봐야 하므로 getFullYear() 를 쓰지 않는다.
 *
 * 규약: 아래 함수는 «KST 벽시계» 를 UTC 필드에 담은 Date 를 다룬다 — `kstNow(x).getUTCHours()` 가 KST 시(時)다.
 *   파일마다 적던 `new Date(Date.now() + 9 * 3600_000)` · `todayKST()` 사본을 이 파일로 모은다(HC-L-KST 상한 게이트).
 * ⚠️ cl-core 는 Deno 에서도 읽힌다 — 외부 패키지 · Node API · '@/' 별칭 import 금지.
 */
export const KST_OFFSET_MS = 9 * 3600_000;
const DAY_MS = 86_400_000;

type At = Date | number;
const msOf = (at: At): number => (typeof at === 'number' ? at : at.getTime());

/** KST 벽시계 Date — UTC 필드(getUTC*)가 KST 값이다. */
export function kstNow(at: At = Date.now()): Date {
  return new Date(msOf(at) + KST_OFFSET_MS);
}

/** KST 기준 연도(예: 2026). */
export function kstYear(now: At = new Date()): number {
  return kstNow(now).getUTCFullYear();
}

/** KST 날짜 'YYYY-MM-DD'. */
export function kstYmd(at: At = Date.now()): string {
  return kstNow(at).toISOString().slice(0, 10);
}

/** KST 날짜('YYYY-MM-DD') 0시의 UTC ISO — DB timestamptz 비교용(`created_at >= …`). */
export function kstDateStartIso(day: string): string {
  return new Date(Date.parse(`${day}T00:00:00Z`) - KST_OFFSET_MS).toISOString();
}

/** KST 오늘 0시의 UTC ISO(파일마다 적던 `new Date(`${kstYmd()}T00:00:00+09:00`)` 사본의 정본). */
export function kstDayStartIso(at: At = Date.now()): string {
  return kstDateStartIso(kstYmd(at));
}

/**
 * 'YYYY-MM-DD' 에 n 일을 더한 'YYYY-MM-DD'(달력 계산 · KST 는 서머타임이 없어 24h = 하루).
 *   못 읽는 날짜는 RangeError(종전 ads/period · ops-core · inflow/config 사본과 같은 동작).
 */
export function addDaysYmd(day: string, n: number): string {
  return new Date(Date.parse(`${day}T00:00:00Z`) + n * DAY_MS).toISOString().slice(0, 10);
}

/** KST 연월 'YYYY-MM'. */
export function kstYm(at: At = Date.now()): string {
  return kstNow(at).toISOString().slice(0, 7);
}

/** KST 시(0~23). */
export function kstHour(at: At = Date.now()): number {
  return kstNow(at).getUTCHours();
}

/**
 * D-day — 대상 날짜(앞 10자 'YYYY-MM-DD' · Date)와 «KST 오늘» 의 달력 일수 차. 오늘 0 · 내일 1 · 어제 -1. 못 읽으면 null.
 *   ⚠️ 서버 로컬 자정(setHours(0))으로 자르면 UTC 서버에서 KST 00:00~09:00 사이에 하루가 밀린다 — 날짜 문자열끼리 뺀다.
 */
export function ddayKst(target: string | Date | null | undefined, now: At = Date.now()): number | null {
  if (target == null || target === '') return null;
  const ymd = target instanceof Date
    ? (Number.isFinite(target.getTime()) ? kstYmd(target) : null)
    : (/^(\d{4})-(\d{2})-(\d{2})/.exec(String(target).trim())?.[0] ?? null);
  if (!ymd) return null;
  const t = Date.parse(`${ymd}T00:00:00Z`);
  if (!Number.isFinite(t)) return null;
  const today = Date.parse(`${kstYmd(now)}T00:00:00Z`);
  return Math.round((t - today) / DAY_MS);
}
